/**
 * Standalone Hotels Portal - Login Page
 * 
 * Direct login to Hotels dashboard - NO industry selector.
 * Full bilingual support (EN/FR).
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Building2, Loader2, Eye, EyeOff, Globe } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { toast } from 'sonner';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';
const API_URL = `${API_BASE}/api/hotels-standalone`;

export default function StandaloneLogin() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  // Force dark mode for standalone pages
  useEffect(() => {
    document.documentElement.classList.add('dark');
    return () => {
      const savedTheme = localStorage.getItem('yul_theme_preference');
      if (savedTheme !== 'dark') {
        document.documentElement.classList.remove('dark');
      }
    };
  }, []);

  const toggleLanguage = () => {
    const newLang = i18n.language === 'en' ? 'fr' : 'en';
    i18n.changeLanguage(newLang);
    localStorage.setItem('yul_language', newLang);
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    
    if (!email || !password) {
      toast.error(i18n.language === 'fr' ? 'Veuillez entrer courriel et mot de passe' : 'Please enter email and password');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || (i18n.language === 'fr' ? 'Échec de connexion' : 'Login failed'));
      }

      // Store token and user
      localStorage.setItem('hotels_token', data.access_token);
      localStorage.setItem('hotels_user', JSON.stringify(data.user));

      toast.success(t('standalone.dashboard.welcomeBack') + '!');
      navigate('/standalone/dashboard');
    } catch (error) {
      toast.error(error.message || (i18n.language === 'fr' ? 'Échec de connexion' : 'Login failed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[hsl(222,47%,11%)] flex items-center justify-center p-4">
      {/* Language Toggle */}
      <div className="absolute top-4 right-4">
        <Button
          variant="ghost"
          size="sm"
          onClick={toggleLanguage}
          className="text-[hsl(215,20%,65%)] hover:text-white"
        >
          <Globe className="w-4 h-4 mr-2" />
          {i18n.language === 'en' ? 'FR' : 'EN'}
        </Button>
      </div>

      <Card className="w-full max-w-md bg-[hsl(217,19%,12%)] border-[hsl(217,19%,27%)]">
        <CardHeader className="text-center">
          <div className="mx-auto w-16 h-16 rounded-2xl bg-blue-600 flex items-center justify-center mb-4">
            <Building2 className="w-8 h-8 text-white" />
          </div>
          <CardTitle className="text-2xl font-bold text-white">HOTELS ON TAP</CardTitle>
          <CardDescription className="text-[hsl(215,20%,65%)]">
            {t('standalone.login.title')} - {t('standalone.login.subtitle')}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleLogin} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-white">{t('standalone.login.email')}</Label>
              <Input
                id="email"
                type="email"
                placeholder={t('standalone.login.emailPlaceholder')}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="bg-[hsl(217,19%,17%)] border-[hsl(217,19%,27%)] text-white"
                disabled={loading}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="text-white">{t('standalone.login.password')}</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="bg-[hsl(217,19%,17%)] border-[hsl(217,19%,27%)] text-white pr-10"
                  disabled={loading}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[hsl(215,20%,65%)]"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <Button
              type="submit"
              className="w-full bg-blue-600 hover:bg-blue-700"
              disabled={loading}
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {t('standalone.login.signingIn')}
                </>
              ) : (
                t('standalone.login.signIn')
              )}
            </Button>
          </form>
          
          {/* Demo credentials hint */}
          <div className="mt-6 p-4 rounded-lg bg-[hsl(217,19%,17%)] border border-[hsl(217,19%,27%)]">
            <p className="text-xs text-[hsl(215,20%,65%)] font-medium mb-2">{t('standalone.login.demoCredentials')}:</p>
            <p className="text-xs text-[hsl(215,20%,65%)]">
              {t('standalone.login.superAdmin')}: <span className="text-blue-400">test@hotels.com</span>
            </p>
          </div>
          <p className="text-center text-[10px] text-[hsl(215,20%,40%)] mt-4" data-testid="build-version">Build: 2026-03-01-v3</p>
        </CardContent>
      </Card>
    </div>
  );
}
